/**
 * Web Health finding row: the twin of `HealthFindingRow.tsx`.
 *
 * A plain row the user reads without opening it: the problem in their own
 * words (bold, wrapping), under it the muted line saying what happens to it
 * (with a quiet "Optional" for an optional finding), and a subtle chevron. No
 * icon, no dot, no badge. Expanded (one at a time, state owned by the list):
 * "Now" with the user's own excerpt (`dir="auto"`, selectable,
 * `ph-no-capture`), then "After the fix" with the proposed text when a
 * proposal exists, or "What we'll ask you" with the fix line when it does
 * not, then the editor's actions for the finding kind and any
 * `extraActions`. The body opens with the grid-rows disclosure
 * (`.tabbio-health-disclosure`), so it animates to its natural height.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import {
  getFindingActions,
  getFindingPlainFixKey,
  getFindingPlainKey,
  type HealthFindingAction,
} from './cv-health-copy';
import type { HealthFindingRowProps } from './health-props';

export type { HealthFindingRowProps };

export function HealthFindingRow({
  finding,
  variant,
  expanded,
  onToggle,
  onPreviewChange,
  onDecision,
  onAddProofLink,
  busyFindingKey,
  extraActions,
  icon,
  subtitle,
  appearance = 'grouped',
}: HealthFindingRowProps) {
  const { t } = useTranslation('common');
  const bodyId = `tabbio-health-finding-${useId().replace(/:/g, '')}`;

  const busy = busyFindingKey === finding.key;
  const plain = t(getFindingPlainKey(finding));
  const plainFix = t(getFindingPlainFixKey(finding));
  const optionalLabel = finding.severity === 'optional' ? t('cvHealth.badges.optional') : null;
  // The fix line already says "Optional." where the copy knows it is.
  const fixLine =
    optionalLabel && !plainFix.includes(optionalLabel) ? `${plainFix} ${optionalLabel}` : plainFix;
  const excerpt = finding.evidence.excerpt;
  const proposal = finding.proposal;
  // The proposed text is always shown, so the inline reveal has no job here.
  const actions = getFindingActions(finding, variant, {
    preview: Boolean(onPreviewChange),
    decision: Boolean(onDecision),
    proof: Boolean(onAddProofLink),
  }).filter((action) => action.intent !== 'reveal');

  const run = (action: HealthFindingAction) => {
    switch (action.intent) {
      case 'preview':
        onPreviewChange?.(finding);
        break;
      case 'decision':
        if (action.decision) onDecision?.(finding, action.decision);
        break;
      case 'proof':
        onAddProofLink?.(finding);
        break;
      default:
        break;
    }
  };

  return (
    <div className={`tabbio-health-finding${appearance === 'list' ? ' is-list' : ''}`}>
      <button
        type="button"
        className="tabbio-health-finding-header"
        data-testid="cv-health-simple-row"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        {icon ? <span className="tabbio-health-suggestion-icon">{icon}</span> : null}
        <span className="tabbio-health-finding-copy">
          <span className="tabbio-health-finding-title">{plain}</span>
          <span className="tabbio-health-finding-fix">{subtitle ?? fixLine}</span>
        </span>
        <span className={`tabbio-health-chevron${expanded ? ' is-open' : ''}`}>
          <MaterialSymbol name="expand_more" size={appearance === 'list' ? 14 : 20} />
        </span>
      </button>

      <div
        id={bodyId}
        className={`tabbio-health-disclosure${expanded ? ' is-open' : ''}`}
        aria-hidden={!expanded}
        inert={!expanded}
      >
        <div>
          <div className="tabbio-health-finding-body">
            {excerpt ? (
              <div className="tabbio-health-quote">
                <span className="tabbio-health-label">{t('cvHealth.simple.nowLabel')}</span>
                <p className="tabbio-health-excerpt ph-no-capture" dir="auto">
                  {excerpt}
                </p>
              </div>
            ) : null}

            {proposal ? (
              <div className="tabbio-health-preview">
                <span className="tabbio-health-label">{t('cvHealth.simple.afterLabel')}</span>
                <p className="tabbio-health-excerpt is-strong ph-no-capture" dir="auto">
                  {proposal.after}
                </p>
              </div>
            ) : (
              <div className="tabbio-health-preview">
                <span className="tabbio-health-label">{t('cvHealth.simple.weAskLabel')}</span>
                <p className="tabbio-health-sentence is-strong">{fixLine}</p>
              </div>
            )}

            {actions.length > 0 || extraActions ? (
              <div className="tabbio-health-actions">
                {actions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className={`tabbio-health-btn${action.emphasis === 'primary' ? ' is-primary' : ' is-text'}`}
                    onClick={() => run(action)}
                    disabled={busy}
                  >
                    {t(action.labelKey)}
                  </button>
                ))}
                {extraActions}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HealthFindingRow;
